/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  PlugZap,
  Ruler,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const electricalConfig: SolutionTemplateConfig = {
  tradeName: "Electrical",
  tradeSlug: "electrical",
  hero: {
    badge: "Electrical Business Platform",
    title: "Software for electrical contractors",
    titleHighlight: "from quote to closeout.",
    description:
      "How many device counts did you miss on that last bid? Are your distributor prices current? Is your wire markup actually covering your labor burden? One platform for your costs, estimates, projects, service calls, and invoices.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded electrical costs",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for electrical contractors",
    proofPoints: [
      { icon: PlugZap, text: "Commercial, residential, and service" },
      { icon: DollarSign, text: "Distributor pricing management" },
      { icon: Wrench, text: "Service calls alongside project work" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running an electrical business without a system.",
    items: [
      {
        icon: Clock,
        title: "100+ line items on a commercial bid",
        description:
          "Outlets, switches, panels, breakers, wire by gauge and run length, conduit, boxes, connectors — and every one of them needs a price. You're cross-referencing three distributor catalogs that changed last quarter.",
      },
      {
        icon: DollarSign,
        title: "Distributor prices that change quarterly",
        description:
          "Your supply house updates wire and conduit pricing every quarter. But your estimating spreadsheet still has last year's numbers. You don't find out until the invoice hits.",
      },
      {
        icon: FileText,
        title: "Service calls tracked separately from project work",
        description:
          "Panel upgrades are projects. Troubleshooting calls are service. But they're the same client, same cost database, same labor rates. Why are they in different systems?",
      },
    ],
  },
  stats: [
    { value: "Projects + Service", label: "Both in one system" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From distributor pricing to final invoice.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Import your wire, conduit, device, and panel pricing from your distributor spreadsheets. Set journeyman, apprentice, and helper rates. Build work packages — a panel upgrade bundle (panel + breakers + wire + conduit + labor) you reuse on every bid.",
        icon: PlugZap,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload electrical plans, count devices by floor or zone, and pull in your work packages. 100+ line items organized by work area. AI matches materials from your catalog. Send a branded proposal before the other electrician opens their spreadsheet.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track journeyman hours, log material costs against the estimate, handle change orders. Service calls tracked alongside project work — same client, same cost database.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. See which types of jobs are most profitable. Ask Hank: 'What's my average wire markup across commercial jobs this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that handles high line-item counts, distributor pricing, and electrical-specific labor.",
    items: [
      {
        icon: Layers,
        title: "Work packages for common assemblies",
        description:
          "Panel upgrade (panel + breakers + wire + conduit + labor), service change (meter + disconnect + grounding), lighting circuit (wire + boxes + devices + switches). Reusable, adjustable per job.",
        bullets: [
          "Material + labor bundles with production rates",
          "Handles 100+ line item estimates",
          "Configurable per job",
          "Track usage across bids",
        ],
      },
      {
        icon: Truck,
        title: "Distributor pricing management",
        description:
          "Import pricing from your electrical distributor. When wire prices change quarterly, update once — every estimate uses the current number. Track per-supplier pricing with contact info.",
        bullets: [
          "Import from distributor spreadsheets",
          "Per-supplier pricing",
          "Equipment cost tracking",
          "Subcontractor management",
        ],
      },
      {
        icon: Wrench,
        title: "Service calls in the same system",
        description:
          "Troubleshooting, panel swaps, outlet additions — service calls are tracked as services with visit logging. Same client record, same cost database, same labor rates as your project work.",
        bullets: [
          "Service management with visit tracking",
          "Cost logging per call",
          "Same client and cost data as projects",
          "Service schedule with calendar view",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs by floor and zone",
        description:
          "Upload electrical plans and count devices, measure wire runs, and organize by floor or zone directly on screen. Quantities feed into your estimate through work packages.",
        bullets: [
          "Count devices on plans",
          "Measure wire runs",
          "Organize by floor, wing, or zone",
          "Multi-page plan support",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Distributor prices from last quarter" },
      { icon: X, text: "Device counts on a legal pad" },
      { icon: X, text: "Service calls tracked in a different system" },
      { icon: X, text: "No idea if your wire markup covered labor burden" },
      { icon: X, text: "Proposals that take a week to put together" },
      { icon: X, text: "Journeyman hours tracked on paper" },
    ],
    after: [
      {
        icon: CheckCircle,
        text: "Current distributor pricing in one cost database",
      },
      { icon: CheckCircle, text: "Device counts organized by floor and zone" },
      {
        icon: CheckCircle,
        text: "Service calls and projects in the same system",
      },
      { icon: CheckCircle, text: "Budget vs. actuals on every job" },
      { icon: CheckCircle, text: "Branded proposals sent the same day" },
      { icon: CheckCircle, text: "Time entries with approval workflow" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your distributor pricing feeds into estimates, your actuals feed back, and your service calls are tracked alongside project work.",
    metrics: [
      {
        value: "Accurate",
        label: "Estimates from current distributor pricing",
      },
      { value: "Faster", label: "100+ line item bids in hours, not days" },
      { value: "Visible", label: "Margin per job — projects and service" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions electrical contractors ask",
    items: [
      {
        question: "Can it handle 100+ line item estimates?",
        answer:
          "Yes. Organize by work area (floor, zone, wing). Use work packages to add common assemblies in bulk. The builder handles detailed estimates with inline editing, bulk actions, and AI-suggested pricing.",
      },
      {
        question: "Can I import my distributor pricing?",
        answer:
          "Yes. Import from Excel or CSV. Map wire gauges, conduit types, devices, and panels into your cost database. Update when prices change — every estimate uses the new numbers.",
      },
      {
        question: "Does it handle service calls?",
        answer:
          "Yes. Service calls are tracked as services with visit logging and cost tracking. Same client record, same cost database, same labor rates as your project estimates.",
      },
      {
        question: "How do work packages work for electrical?",
        answer:
          "Build reusable bundles — a 'Panel Upgrade' package includes the panel, breakers, wire, conduit, and installation labor. Pull it into any estimate and adjust for the specific job.",
      },
      {
        question: "Can I track journeyman hours per job?",
        answer:
          "Yes. Time entries with approval workflow. Labor costs logged against the estimate for budget vs. actuals.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, foremen, or office staff. Role-based access controls who sees what.",
      },
    ],
  },
  cta: {
    title: "Run your electrical business from one platform.",
    description:
      "Distributor pricing, device counts, service calls, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded electrical costs",
      "Cancel anytime",
    ],
  },
};
